import React, { useState } from "react";
import { Music, ArrowLeft, Send } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import api from "./api";

function formatErr(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Something went wrong. Please try again.";
}

export default function ForgotPassword({ onBack }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    setMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message || "If that email exists, a reset link has been generated.");
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6 font-sans">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-indigo-600 to-violet-500 flex items-center justify-center text-white shadow-lg shadow-indigo-200">
            <Music className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold tracking-tight text-slate-900 leading-tight">Melody Teacher</h1>
            <p className="text-xs text-slate-500">Music Class Manager</p>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl shadow-xl p-8 space-y-5" data-testid="forgot-form">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Reset your password</h2>
            <p className="text-xs text-slate-500 mt-1">
              Enter the email tied to your teacher account. If email delivery isn't wired yet, your studio admin can either share the reset link from the server logs or generate a new temporary password for you from the Teachers panel.
            </p>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 block">Email</label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@melodyteacher.com" className="h-11 border-slate-200" data-testid="forgot-email-input" />
          </div>

          {msg && (
            <div className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="forgot-success">
              {msg}
            </div>
          )}
          {err && (
            <div className="text-xs font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="forgot-error">
              {err}
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-bold gap-2" data-testid="forgot-submit-btn">
            <Send className="w-4 h-4" />
            {loading ? "Sending…" : "Send reset link"}
          </Button>

          <button type="button" onClick={onBack} className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-slate-600 hover:text-indigo-600 pt-2" data-testid="forgot-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </button>
        </form>
      </div>
    </div>
  );
}
